import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  IndianRupee,
  ClipboardList,
  Users,
  Star,
  Download,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  Share2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const RANGE_OPTIONS = [
  { key: 'today' },
  { key: 'week' },
  { key: 'month' },
  { key: 'custom' },
];

const SUMMARY_KEYS = [
  { key: 'revenue', icon: IndianRupee, color: 'bg-[#f0a500]', textColor: 'text-[#1a1a2e]' },
  { key: 'bookings', icon: ClipboardList, color: 'bg-[#1a1a2e]', textColor: 'text-white' },
  { key: 'new_customers', icon: Users, color: 'bg-emerald-500', textColor: 'text-white' },
  { key: 'avg_bill_value', icon: Star, color: 'bg-purple-500', textColor: 'text-white' },
];

const PAYMENT_COLORS = {
  Cash: '#f0a500',
  Card: '#1a1a2e',
  UPI: '#6b8f71',
};

const CUSTOMER_COLORS = ['#1a1a2e', '#f0a500'];

const roleColors = {
  Owner: 'bg-purple-100 text-purple-700',
  Manager: 'bg-blue-100 text-blue-700',
  Stylist: 'bg-green-100 text-green-700',
  Assistant: 'bg-orange-100 text-orange-700',
  Receptionist: 'bg-pink-100 text-pink-700',
};

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const tooltipStyles = {
  contentStyle: {
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
  },
  cursor: { fill: 'rgba(240, 165, 0, 0.08)' },
};

function getDefaultCustomDates() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return [start.toISOString().slice(0, 10), today.toISOString().slice(0, 10)];
}

function getDateRangeForSummary(selectedRange, customStart, customEnd) {
  const today = new Date();
  const toYmd = (d) => d.toISOString().slice(0, 10);
  if (selectedRange === 'custom' && customStart && customEnd) {
    return { start_date: customStart, end_date: customEnd };
  }
  if (selectedRange === 'today') {
    const d = toYmd(today);
    return { start_date: d, end_date: d };
  }
  if (selectedRange === 'week') {
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    return { start_date: toYmd(start), end_date: toYmd(today) };
  }
  if (selectedRange === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start_date: toYmd(start), end_date: toYmd(today) };
  }
  return { start_date: toYmd(new Date(today.getFullYear(), today.getMonth(), 1)), end_date: toYmd(today) };
}

export default function Reports() {
  const { t } = useLanguage();
  const { salonName: profileSalonName } = useSalonProfile();
  const user = JSON.parse(localStorage.getItem('salon_user') || '{}');
  const [selectedRange, setSelectedRange] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productSummary, setProductSummary] = useState([]);
  const [productSummaryLoading, setProductSummaryLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const customRangeLoadedRef = useRef(false);

  const loadProductSummary = useCallback(async () => {
    const { start_date, end_date } = getDateRangeForSummary(selectedRange, customStart, customEnd);
    setProductSummaryLoading(true);
    try {
      const res = await api.get('/bill-products/summary', { params: { start_date, end_date } });
      setProductSummary(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('rpt_reports_load_error'));
      setProductSummary([]);
    } finally {
      setProductSummaryLoading(false);
    }
  }, [selectedRange, customStart, customEnd, t]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params = { period: selectedRange };
      if (selectedRange === 'custom' && customStart && customEnd) {
        params.start_date = customStart;
        params.end_date = customEnd;
      }
      const response = await api.get('/reports/analytics', { params });
      setAnalytics(response.data);
      loadProductSummary();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.loc?.join('.')).join(', ')
        : typeof detail === 'string'
          ? detail
          : err.message || t('rpt_reports_load_error');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // When switching to Custom, set default range to current month so we can load immediately
  useEffect(() => {
    if (selectedRange === 'custom' && !customStart && !customEnd) {
      const [start, end] = getDefaultCustomDates();
      setCustomStart(start);
      setCustomEnd(end);
      customRangeLoadedRef.current = false;
    }
    if (selectedRange !== 'custom') customRangeLoadedRef.current = false;
  }, [selectedRange]);

  useEffect(() => {
    if (selectedRange !== 'custom') {
      loadAnalytics();
    } else if (customStart && customEnd && !customRangeLoadedRef.current) {
      customRangeLoadedRef.current = true;
      loadAnalytics();
    }
  }, [selectedRange, customStart, customEnd, loadProductSummary]);

  const handleApplyCustom = () => {
    if (!customStart || !customEnd || customStart > customEnd) {
      toast.error(t('rpt_select_start_date') + ' / ' + t('rpt_select_end_date'));
      return;
    }
    loadAnalytics();
  };

  const effectivePeriodLabel = useMemo(() => {
    if (selectedRange !== 'custom' || !customStart || !customEnd) return null;
    try {
      const d1 = format(parseISO(customStart), 'd MMM yyyy');
      const d2 = format(parseISO(customEnd), 'd MMM yyyy');
      return `${d1} – ${d2}`;
    } catch {
      return null;
    }
  }, [selectedRange, customStart, customEnd]);

  const paymentTotal = useMemo(
    () => (analytics?.payment_methods || []).reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [analytics],
  );

  const customerTotal = useMemo(
    () => (analytics?.customer_split || []).reduce((sum, item) => sum + Number(item.count || 0), 0),
    [analytics],
  );

  const maxTopServiceRevenue = useMemo(
    () => Math.max(...(analytics?.top_services || []).map((item) => Number(item.revenue || 0)), 0),
    [analytics],
  );

  const exportFileNameBase = () => {
    const period = selectedRange === 'custom' && customStart && customEnd
      ? `${customStart}_${customEnd}`
      : selectedRange;
    return `SalonSync_Report_${period}_${new Date().toISOString().slice(0, 10)}`;
  };

  const handleExportCsv = () => {
    setExportOpen(false);
    if (!analytics?.revenue_trend?.length) {
      toast.error(t('rpt_export_no_data'));
      return;
    }
    const rows = analytics.revenue_trend.map((item) => [
      item.date,
      item.bookings ?? 0,
      item.revenue ?? 0,
    ]);
    const csv = [
      [t('rpt_date'), t('rpt_bookings'), t('rpt_revenue')],
      ...rows,
    ]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${exportFileNameBase()}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success(t('rpt_export_csv_done'));
  };

  const handleExportExcel = () => {
    setExportOpen(false);
    if (!analytics) {
      toast.error(t('rpt_export_no_data'));
      return;
    }
    const wb = XLSX.utils.book_new();
    const summary = [
      [t('rpt_period'), selectedRange === 'custom' && effectivePeriodLabel ? effectivePeriodLabel : selectedRange],
      [t('rpt_total_revenue'), analytics.summary?.revenue ?? 0],
      [t('rpt_total_bookings'), analytics.summary?.bookings ?? 0],
      [t('rpt_new_customers'), analytics.summary?.new_customers ?? 0],
      [t('rpt_avg_bill_value'), analytics.summary?.avg_bill_value ?? 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
    const detailRows = (analytics.revenue_trend || []).map((row) => {
      const paymentBreak = (analytics.payment_methods || []).reduce((acc, p) => ({ ...acc, [p.name]: p.amount }), {});
      const topSvc = (analytics.top_services || [])[0]?.name || '';
      return [
        row.date,
        row.bookings ?? 0,
        row.revenue ?? 0,
        topSvc,
        paymentBreak.Cash ?? 0,
        paymentBreak.Card ?? 0,
        paymentBreak.UPI ?? 0,
      ];
    });
    const detailHeader = [t('rpt_date'), t('rpt_bookings'), t('rpt_revenue'), t('rpt_top_service'), t('rpt_cash'), t('rpt_card'), t('rpt_upi')];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([detailHeader, ...detailRows]), 'Details');
    XLSX.writeFile(wb, `${exportFileNameBase()}.xlsx`);
    toast.success(t('rpt_export_csv_done'));
  };

  const generatePdfBlob = () => {
    const doc = new jsPDF();
    const salonName = profileSalonName || user.salon_name || 'Salon';
    let y = 16;
    doc.setFontSize(18);
    doc.text('SalonSync', 14, y);
    y += 8;
    doc.setFontSize(12);
    doc.text(salonName, 14, y);
    y += 6;
    doc.setFontSize(10);
    doc.text(t('rpt_generated_on') + ': ' + format(new Date(), 'd MMM yyyy, HH:mm'), 14, y);
    y += 12;

    autoTable(doc, {
      startY: y,
      head: [[t('rpt_total_revenue'), t('rpt_total_bookings'), t('rpt_new_customers'), t('rpt_avg_bill_value')]],
      body: [[
        (analytics?.summary?.revenue ?? 0).toLocaleString('en-IN'),
        (analytics?.summary?.bookings ?? 0).toLocaleString('en-IN'),
        (analytics?.summary?.new_customers ?? 0).toLocaleString('en-IN'),
        (analytics?.summary?.avg_bill_value ?? 0).toLocaleString('en-IN'),
      ]],
      theme: 'grid',
      headStyles: { fillColor: [26, 26, 46] },
    });
    y = doc.lastAutoTable.finalY + 10;

    autoTable(doc, {
      startY: y,
      head: [[t('rpt_service'), t('rpt_bookings'), t('rpt_revenue')]],
      body: (analytics?.top_services || []).map((s) => [s.name, String(s.bookings ?? 0), String(s.revenue ?? 0)]),
      theme: 'grid',
      headStyles: { fillColor: [26, 26, 46] },
    });
    y = doc.lastAutoTable.finalY + 10;

    autoTable(doc, {
      startY: y,
      head: [[t('rpt_name'), t('rpt_role'), t('rpt_appointments'), t('rpt_revenue'), t('rpt_commission_earned')]],
      body: (analytics?.staff_performance || []).map((m) => [
        m.staff_name || '',
        m.role || '',
        String(m.appointments ?? 0),
        String(m.revenue ?? 0),
        String(m.commission_earned ?? 0),
      ]),
      theme: 'grid',
      headStyles: { fillColor: [26, 26, 46] },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(t('rpt_generated_by') + ' — ' + i + '/' + pageCount, 14, doc.internal.pageSize.height - 10);
    }
    return doc.output('blob');
  };

  const handleExportPdf = () => {
    setExportOpen(false);
    if (!analytics) {
      toast.error(t('rpt_export_no_data'));
      return;
    }
    const blob = generatePdfBlob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${exportFileNameBase()}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success(t('rpt_export_csv_done'));
  };

  const handleShareReport = async () => {
    setExportOpen(false);
    if (!analytics) {
      toast.error(t('rpt_export_no_data'));
      return;
    }
    const periodLabel = selectedRange === 'custom' && effectivePeriodLabel ? effectivePeriodLabel : selectedRange;
    if (navigator.share) {
      try {
        const blob = generatePdfBlob();
        const file = new File([blob], `${exportFileNameBase()}.pdf`, { type: 'application/pdf' });
        await navigator.share({
          title: 'SalonSync Report',
          text: t('rpt_period') + ': ' + periodLabel,
          files: [file],
        });
        toast.success(t('rpt_export_csv_done'));
      } catch (e) {
        if (e.name !== 'AbortError') copySummaryFallback(periodLabel);
      }
    } else {
      copySummaryFallback(periodLabel);
    }
  };

  const copySummaryFallback = (periodLabel) => {
    const s = analytics?.summary;
    const topSvc = (analytics?.top_services || [])[0]?.name || '—';
    const text = `SalonSync Report — ${periodLabel}\n${t('rpt_revenue')}: ₹${(s?.revenue ?? 0).toLocaleString('en-IN')}\n${t('rpt_bookings')}: ${s?.bookings ?? 0}\n${t('rpt_top_service')}: ${topSvc}\n${t('rpt_generated_by')}`;
    navigator.clipboard.writeText(text).then(() => toast.success(t('rpt_summary_copied')));
  };

  const summaryCardLabels = {
    revenue: t('rpt_total_revenue'),
    bookings: t('rpt_total_bookings'),
    new_customers: t('rpt_new_customers'),
    avg_bill_value: t('rpt_avg_bill_value'),
  };
  const summaryCards = SUMMARY_KEYS.map((item) => ({
    ...item,
    label: summaryCardLabels[item.key],
    value: item.key === 'revenue' || item.key === 'avg_bill_value'
      ? formatCurrency(analytics?.summary?.[item.key])
      : Number(analytics?.summary?.[item.key] || 0).toLocaleString('en-IN'),
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-10 w-64 rounded-2xl" />
            <Skeleton className="h-5 w-44 rounded-xl" />
          </div>
          <div className="flex flex-wrap gap-2">
            {RANGE_OPTIONS.map((option) => (
              <Skeleton key={option.key} className="h-11 w-28 rounded-xl" />
            ))}
            <Skeleton className="h-11 w-36 rounded-xl" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-36 rounded-2xl" />)}
        </div>

        <Skeleton className="h-[360px] rounded-2xl" />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Skeleton className="h-[360px] rounded-2xl" />
          <Skeleton className="h-[360px] rounded-2xl" />
        </div>
        <Skeleton className="h-[420px] rounded-2xl" />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Skeleton className="h-[320px] rounded-2xl" />
          <Skeleton className="h-[320px] rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <h1 className="font-poppins text-2xl lg:text-3xl font-bold text-[#1a1a2e]">{t('rpt_title')}</h1>
          <p className="text-gray-500 mt-1">{t('rpt_subtitle')}</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 xl:justify-end items-start xl:items-center">
          <Tabs
            value={selectedRange}
            onValueChange={(value) => {
              setSelectedRange(value);
              if (value === 'custom') {
                const [s, e] = getDefaultCustomDates();
                setCustomStart(s);
                setCustomEnd(e);
              }
            }}
            className="w-full xl:w-auto"
          >
            <TabsList className="bg-white rounded-xl p-1 h-auto flex flex-wrap gap-1 border border-gray-200">
              <TabsTrigger value="today" className="rounded-lg px-4 py-2.5 text-sm data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">{t('rpt_today')}</TabsTrigger>
              <TabsTrigger value="week" className="rounded-lg px-4 py-2.5 text-sm data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">{t('rpt_this_week')}</TabsTrigger>
              <TabsTrigger value="month" className="rounded-lg px-4 py-2.5 text-sm data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">{t('rpt_this_month')}</TabsTrigger>
              <TabsTrigger value="custom" className="rounded-lg px-4 py-2.5 text-sm data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">{t('rpt_custom')}</TabsTrigger>
            </TabsList>
          </Tabs>
          {selectedRange === 'custom' && (
            <div className="flex flex-wrap items-end gap-3 w-full xl:w-auto">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-gray-500">{t('rpt_select_start_date')}</Label>
                <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-11 rounded-xl w-full min-w-[140px]" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-gray-500">{t('rpt_select_end_date')}</Label>
                <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-11 rounded-xl w-full min-w-[140px]" />
              </div>
              <Button type="button" onClick={handleApplyCustom} className="h-11 px-5 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold">
                {t('rpt_apply')}
              </Button>
              {effectivePeriodLabel && <span className="text-sm text-gray-600 self-center">{effectivePeriodLabel}</span>}
            </div>
          )}

          <DropdownMenu open={exportOpen} onOpenChange={setExportOpen}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="h-11 px-4 rounded-xl border-gray-300 text-[#1a1a2e] bg-transparent hover:bg-white gap-2">
                <Download className="w-4 h-4" /> {t('rpt_export')} <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl min-w-[200px]">
              <DropdownMenuItem onClick={handleExportCsv} className="rounded-lg gap-2 cursor-pointer">
                <FileText className="w-4 h-4" /> {t('rpt_export_csv')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel} className="rounded-lg gap-2 cursor-pointer">
                <FileSpreadsheet className="w-4 h-4" /> {t('rpt_export_excel')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPdf} className="rounded-lg gap-2 cursor-pointer">
                <FileText className="w-4 h-4" /> {t('rpt_export_pdf')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleShareReport} className="rounded-lg gap-2 cursor-pointer">
                <Share2 className="w-4 h-4" /> {t('rpt_share_report')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!analytics?.has_data && (
        <Card className="border border-dashed border-[#f0a500]/40 bg-[#fffaf0] shadow-none rounded-2xl">
          <CardContent className="px-5 py-4 text-sm font-medium text-[#7c5a00]">
            {t('rpt_no_data')}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {summaryCards.map((card, index) => (
          <Card
            key={card.key}
            className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden animate-fade-in-up"
            style={{ animationDelay: `${index * 75}ms` }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`${card.color} ${card.textColor} p-3 rounded-xl`}>
                  <card.icon className="w-6 h-6" />
                </div>
              </div>
              <p className="text-sm text-gray-500 font-medium mb-1">{card.label}</p>
              <p className="font-poppins text-2xl font-bold text-[#1a1a2e]">{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl min-w-0">
        <CardHeader className="pb-2">
          <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_revenue_trend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[320px] min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.revenue_trend || []} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip
                  {...tooltipStyles}
                  formatter={(value) => [formatCurrency(value), t('rpt_revenue')]}
                  labelFormatter={(label) => `${t('rpt_date')}: ${label}`}
                />
                <Bar dataKey="revenue" fill="#f0a500" radius={[10, 10, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_top_services')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(analytics?.top_services?.length || 0) > 0 ? (
              analytics.top_services.map((service, index) => {
                const width = maxTopServiceRevenue > 0
                  ? `${Math.max((Number(service.revenue || 0) / maxTopServiceRevenue) * 100, 12)}%`
                  : '12%';

                return (
                  <div key={service.name} className="rounded-2xl bg-[#faf8f5] p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#1a1a2e] text-white text-sm font-semibold flex items-center justify-center">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-[#1a1a2e]">{service.name}</p>
                          <p className="text-sm text-gray-500">{service.bookings} {t('rpt_times_booked')}</p>
                        </div>
                      </div>
                      <p className="font-semibold text-[#1a1a2e]">{formatCurrency(service.revenue)}</p>
                    </div>
                    <div className="h-2 rounded-full bg-white overflow-hidden">
                      <div className="h-full rounded-full bg-[#f0a500]" style={{ width }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="h-[260px] flex items-center justify-center text-gray-400 text-base">
                {t('rpt_no_data')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_payment_methods')}</CardTitle>
          </CardHeader>
          <CardContent>
            {paymentTotal > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-6 items-center min-w-0">
                <div className="h-[240px] min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics?.payment_methods || []}
                        dataKey="amount"
                        nameKey="name"
                        innerRadius={65}
                        outerRadius={92}
                        paddingAngle={3}
                      >
                        {(analytics?.payment_methods || []).map((item) => (
                          <Cell key={item.name} fill={PAYMENT_COLORS[item.name] || '#cbd5e1'} />
                        ))}
                      </Pie>
                      <Tooltip
                        {...tooltipStyles}
                        formatter={(value, _, entry) => [formatCurrency(value), entry?.payload?.name ? t('rpt_' + entry.payload.name.toLowerCase()) : t('rpt_payment')]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-3">
                  {(analytics?.payment_methods || []).map((item) => (
                    <div key={item.name} className="flex items-center justify-between gap-6 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: PAYMENT_COLORS[item.name] || '#cbd5e1' }}
                        />
                        <span className="text-sm font-medium text-[#1a1a2e]">{item.name === 'Cash' ? t('rpt_cash') : item.name === 'Card' ? t('rpt_card') : t('rpt_upi')}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[#1a1a2e]">{item.percentage}%</p>
                        <p className="text-xs text-gray-500">{formatCurrency(item.amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-gray-400 text-base">
                {t('rpt_no_data')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_staff_performance')}</CardTitle>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-4 px-4 font-medium">{t('rpt_staff')}</th>
                  <th className="pb-4 px-4 font-medium">{t('rpt_appointments')}</th>
                  <th className="pb-4 px-4 font-medium">{t('rpt_revenue_generated')}</th>
                  <th className="pb-4 px-4 font-medium">{t('rpt_commission_earned')}</th>
                  <th className="pb-4 px-4 font-medium">{t('rpt_top_service')}</th>
                </tr>
              </thead>
              <tbody>
                {(analytics?.staff_performance || []).map((member, index) => (
                  <tr
                    key={member.staff_id || member.staff_name}
                    className={index % 2 === 0 ? 'bg-[#faf8f5]/70' : 'bg-white'}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-[#1a1a2e] text-white font-semibold flex items-center justify-center">
                          {member.staff_name?.[0] || 'S'}
                        </div>
                        <div>
                          <p className="font-semibold text-[#1a1a2e]">{member.staff_name}</p>
                          <Badge className={roleColors[member.role] || roleColors.Stylist}>{member.role}</Badge>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 font-medium text-[#1a1a2e]">{member.appointments}</td>
                    <td className="px-4 py-4">
                      <span className="font-semibold text-[#1a1a2e]">{formatCurrency(member.revenue)}</span>
                      {((member.bills_count ?? 0) > 0 || (member.bookings_count ?? 0) > 0) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatCurrency(member.revenue)} {t('staff_performance_bills')}
                          {(member.bookings_count ?? 0) > 0 && ` · ${member.bookings_count} ${t('staff_performance_bookings')}`}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-gray-600">{formatCurrency(member.commission_earned)}</td>
                    <td className="px-4 py-4 text-gray-600">{member.top_service}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_product_usage')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {productSummaryLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-xl" />
              ))}
            </div>
          ) : (productSummary?.length || 0) > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px] text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="pb-3 px-4 font-medium">{t('rpt_product_name')}</th>
                    <th className="pb-3 px-4 font-medium text-right">{t('rpt_quantity_used')}</th>
                    <th className="pb-3 px-4 font-medium text-right">{t('rpt_total_cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {productSummary.map((row) => (
                    <tr key={row.product_id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3 font-medium text-[#1a1a2e]">{row.product_name}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{Number(row.total_quantity_used ?? 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#1a1a2e]">{formatCurrency(row.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[120px] flex items-center justify-center text-gray-400 text-base">
              {t('rpt_no_data')}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_returning_vs_new')}</CardTitle>
          </CardHeader>
          <CardContent>
            {customerTotal > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-6 items-center min-w-0">
                <div className="h-[220px] min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics?.customer_split || []}
                        dataKey="count"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={82}
                        paddingAngle={3}
                      >
                        {(analytics?.customer_split || []).map((item, index) => (
                          <Cell key={item.name} fill={CUSTOMER_COLORS[index % CUSTOMER_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        {...tooltipStyles}
                        formatter={(value, _, entry) => [value, entry?.payload?.name === 'Returning' ? t('rpt_returning') : t('rpt_new')]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-3">
                  {(analytics?.customer_split || []).map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between gap-6 min-w-[210px]">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: CUSTOMER_COLORS[index % CUSTOMER_COLORS.length] }}
                        />
                        <span className="text-sm font-medium text-[#1a1a2e]">{item.name === 'Returning' ? t('rpt_returning') : t('rpt_new')}</span>
                      </div>
                      <p className="text-sm font-semibold text-[#1a1a2e]">{item.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-gray-400 text-base">
                {t('rpt_no_data')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="font-poppins text-lg font-semibold text-[#1a1a2e]">{t('rpt_peak_hours')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.peak_hours || []} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="#94a3b8" interval={1} angle={-35} textAnchor="end" height={52} />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip
                    {...tooltipStyles}
                    formatter={(value) => [value, t('rpt_bookings')]}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Bar dataKey="bookings" fill="#1a1a2e" radius={[8, 8, 0, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
